import {ExecutionResult, MutationResult} from "@apollo/react-common"
import {MutationTuple} from "@apollo/react-hooks"
import {assert, assertDefined, runAsync} from "@prosay/common"
import {translate} from "@prosay/i18n"
import {getLogger} from "@prosay/logging"
import {
    Field as FormikField,
    FieldInputProps, // eslint-disable-line sort-imports
    FieldMetaProps,
    Formik,
    FormikConfig,
    FormikErrors,
    FormikHelpers,
    FormikProps,
} from "formik"
import {DocumentNode, GraphQLError} from "graphql"
import React, {useCallback, useMemo, useState} from "react"
import {StyleSheet, View} from "react-native"
import {HelperText, Text} from "react-native-paper"
import {getValidationSchema} from "../util"

const logger = getLogger(__filename)

const styles = StyleSheet.create({
    errorContainer: {
        backgroundColor: "#ffffff",
    },
    errorText: {
        textAlign: "center",
    },
    errorTitle: {
        color: "red",
        fontWeight: "bold",
        marginBottom: 5,
        marginTop: 10,
        textAlign: "center",
    },
})

export interface GenericFormErrorTextProps {
    errors?: string[]
}
export const GenericFormErrorText: React.FC<GenericFormErrorTextProps> = ({
    errors,
}) => {
    if (!errors) {
        return null
    }

    return (
        <View style={styles.errorContainer}>
            <Text style={styles.errorTitle}>
                {errors.length === 1
                    ? translate("ui.form.errorSingular")
                    : translate("ui.form.errorPlural")}
            </Text>

            {errors.map((error, i) => (
                <HelperText
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${i}:${error}`}
                    style={styles.errorText}
                    type="error"
                    visible>
                    {error}
                </HelperText>
            ))}
        </View>
    )
}

type GetMutationTuple<
    TMutation extends MutationTuple<any, any> // eslint-disable-line @typescript-eslint/no-explicit-any
> = TMutation extends MutationTuple<infer TData, infer TVariables>
    ? {data: TData; variables: TVariables}
    : never

export function defaultProcessError<TVariables>(
    error: GraphQLError,
): FormikErrors<TVariables> {
    if (
        !error.extensions ||
        !("fields" in error.extensions) ||
        !Array.isArray(error.extensions.fields)
    ) {
        return {}
    }

    return (Object.fromEntries(
        (error.extensions.fields as readonly string[]).map(
            key => [key, error.message] as const,
        ),
    ) as unknown) as FormikErrors<TVariables>
}

export interface FormProps<
    TData,
    TVariables,
    TMutation extends MutationTuple<TData, TVariables>
> extends Omit<FormikConfig<TVariables>, "onSubmit" | "children"> {
    additionalErrors?:
        | readonly (GraphQLError | string | undefined)[]
        | (GraphQLError | string | undefined)[]
        | undefined
    autoReset?: boolean
    children:
        | ((
              props: FormikProps<TVariables> & {
                  mutationErrors: string[] | undefined
                  mutationResult: MutationResult<TData>
              },
          ) => React.ReactNode)
        | React.ReactNode
    mutation: TMutation
    noErrors?: boolean
    onSuccess?: (
        data: TData,
        result: ExecutionResult<TData>,
    ) => void | Promise<void>
    processError?: (
        error: GraphQLError,
    ) => FormikErrors<TVariables> | Promise<FormikErrors<TVariables>>
}
export function Form<
    TMutation extends MutationTuple<any, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    TData extends GetMutationTuple<TMutation>["data"],
    TVariables extends GetMutationTuple<TMutation>["variables"]
>({
    mutation: [mutate, mutationResult, ...restMutation],
    children,
    onSuccess,
    processError,
    additionalErrors = [],
    autoReset = false,
    noErrors = false,
    ...props
}: FormProps<TData, TVariables, TMutation>) {
    const [mutationErrors_, setMutationErrors] = useState<string[]>([])
    const mutationErrors = useMemo(() => {
        const list = [
            ...mutationErrors_,
            ...additionalErrors
                .filter(error => error !== undefined)
                .map(error => {
                    assert(error !== undefined)
                    return typeof error === "string" ? error : error.message
                }),
        ]
        return list.length === 0 ? undefined : list
    }, [additionalErrors, mutationErrors_])
    const document = (restMutation[0] as unknown) as DocumentNode | undefined
    const [validationSchema, operationName] = useMemo(() => {
        assert(document, "Document cannot be undefined!")
        return getValidationSchema<TVariables>(document)
    }, [document])
    const onSubmit = useCallback(
        (values: TVariables, formikHelpers: FormikHelpers<TVariables>) =>
            runAsync(async () => {
                formikHelpers.setSubmitting(true)

                const handleErrors = async (errors: GraphQLError[]) => {
                    logger.info`Got the following errors from the ${operationName} operation: ${errors}.`

                    const formikErrorsDefaultArray = errors.map(error =>
                        defaultProcessError(error),
                    )
                    const formikErrorsDefault = formikErrorsDefaultArray.reduce(
                        (acc, curr) => ({...acc, ...curr}),
                        {},
                    )
                    let untouchedErrors = errors.map((error, i) =>
                        Object.entries(formikErrorsDefaultArray[i]).length === 0
                            ? error
                            : undefined,
                    )

                    let formikErrorsUser: FormikErrors<TVariables> = {}
                    if (processError) {
                        logger.debug`User defined processError is set... Calling processError on the retrieved errors.`
                        const formikErrorsUserArray = await Promise.all(
                            errors.map(
                                async error =>
                                    await Promise.resolve(processError(error)),
                            ),
                        )
                        formikErrorsUser = formikErrorsUserArray.reduce<
                            FormikErrors<TVariables>
                        >(
                            (acc, curr = {}) => ({...acc, ...curr}),
                            {} as FormikErrors<TVariables>,
                        )

                        untouchedErrors = untouchedErrors.map((error, i) =>
                            Object.entries(formikErrorsUserArray[i]).length ===
                            0
                                ? error
                                : undefined,
                        )
                    }

                    // TODO: should we send errors to setMutationErrors if they have already been handled by formikErrors?
                    untouchedErrors = untouchedErrors.filter(
                        error => error !== undefined,
                    )

                    setMutationErrors(
                        untouchedErrors.map(error =>
                            assertDefined(error?.message),
                        ) as string[],
                    )

                    const formikErrors = {
                        ...formikErrorsDefault,
                        ...formikErrorsUser,
                    }
                    formikHelpers.setErrors(formikErrors)
                }

                logger.debug`Attempting to run mutation ${operationName} with the following parameters ${values}`
                try {
                    if (autoReset) {
                        formikHelpers.resetForm(props.initialValues)
                    }

                    const result = await mutate({variables: {...values}})
                    if (result.errors) {
                        await handleErrors(result.errors)
                    } else {
                        assert(
                            result.data,
                            "Expected data to be defined if there are no errors!",
                        )
                        setMutationErrors([])

                        logger.debug`Got the following result from operation ${operationName}: ${result.data}`
                        await Promise.resolve(onSuccess?.(result.data, result))
                    }
                } catch (errors) {
                    if (!errors.graphQLErrors) {
                        throw errors
                    }
                    await handleErrors(errors.graphQLErrors)
                }

                formikHelpers.setSubmitting(false)
            }),
        [
            autoReset,
            mutate,
            onSuccess,
            operationName,
            processError,
            // eslint-disable-next-line react/destructuring-assignment
            props.initialValues,
        ],
    )

    return (
        <Formik<TVariables>
            onSubmit={onSubmit}
            validationSchema={validationSchema}
            {...props}>
            {typeof children === "function" ? (
                options => (
                    <>
                        {!noErrors && (
                            <GenericFormErrorText errors={mutationErrors} />
                        )}
                        {children({
                            ...options,
                            mutationErrors,
                            mutationResult,
                        })}
                    </>
                )
            ) : (
                <>
                    {!noErrors && (
                        <GenericFormErrorText errors={mutationErrors} />
                    )}
                    {children}
                </>
            )}
        </Formik>
    )
}
export interface FieldProps<Parent, Current> {
    field: FieldInputProps<Current> & {
        error?: string
        onChangeText: FieldInputProps<Current>["onChange"]
    }
    form: FormikProps<Parent>
    meta: FieldMetaProps<Current>
}
export interface FieldComponentProps<
    TMutation extends MutationTuple<any, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    TName extends keyof GetMutationTuple<TMutation>["variables"]
> {
    children?:
        | ((
              props: FieldProps<
                  GetMutationTuple<TMutation>["variables"],
                  GetMutationTuple<TMutation>["variables"][TName]
              >,
          ) => React.ReactNode)
        | React.ReactNode
    mutation: TMutation
    name: TName
    value?: GetMutationTuple<TMutation>["variables"][TName]
}
export function Field<
    TMutation extends MutationTuple<any, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    TName extends keyof GetMutationTuple<TMutation>["variables"]
>({children, ...props}: FieldComponentProps<TMutation, TName>) {
    return (
        <FormikField {...props}>
            {typeof children === "function" ? (
                ({
                    field: {name, onBlur, onChange, ...field},
                    ...options
                }: FieldProps<
                    GetMutationTuple<TMutation>["variables"],
                    GetMutationTuple<TMutation>["variables"][TName]
                >) => {
                    const onChange_ = onChange(name)
                    return (
                        <>
                            {children?.({
                                field: {
                                    error: options.meta.error,
                                    name,
                                    onBlur: onBlur(name),
                                    onChange: onChange_,
                                    onChangeText: onChange_,
                                    ...field,
                                },
                                ...options,
                            })}
                        </>
                    )
                }
            ) : (
                <>{children}</>
            )}
        </FormikField>
    )
}
